import { Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import React, { ReactElement } from "react";

type ModelName = "Timeschedule" | "Product" | "Menu" | "Upsell" | "UpsellFlow" | "Salesarea";

const nameMapping: { [k in ModelName]: string } = {
  Menu: "Menu",
  Product: "Product",
  Upsell: "Upsell",
  Timeschedule: "Time schedule",
  UpsellFlow: "Flow",
  Salesarea: "Salesarea",
};

// const linkMapping: Record<ModelName, (modelId: number) => string> = {
//   Menu: (modelId) => `kaarten/${modelId}/edit`,
//   Product: (modelId) => `producten/${modelId}/edit`,
// };

/**
 * Display a model instance with its name and optionally its ID.
 *
 * @component
 * @param {Object} props - The properties that define the ModelInstance component.
 * @param {Object} props.instance - The instance object containing id and name.
 * @param {number} props.instance.id - The ID of the model instance.
 * @param {string} [props.instance.name] - The name of the model instance (optional).
 * @param {ModelName} props.modelName - The type of the model.
 * @param {"No name available"|"always"|"never"} [props.showIdWhen="No name available"] - Determines when to show the ID.
 *
 * @returns {JSX.Element} A Box component containing the model name and optionally its ID.
 *
 * @example
 * // Basic usage
 * <ModelInstance
 *   instance={{ id: 123, name: "Sample Product" }}
 *   modelName="Product"
 * />
 *
 * @example
 * // Always show ID
 * <ModelInstance
 *   instance={{ id: 456 }}
 *   modelName="Menu"
 *   showIdWhen="always"
 * />
 *
 * @example
 * // Never show ID
 * <ModelInstance
 *   instance={{ id: 789, name: "Sample Upsell" }}
 *   modelName="Upsell"
 *   showIdWhen="never"
 * />
 */
export default function ModelInstance({
  instance: { id, name, ...instance },
  modelName,
  showIdWhen = "No name available",
  mark = "jamezz",
  inheritColor = false,
  renderIdAs = "subtext",
  removed = false,
}: {
  instance: { id: number; name?: string };
  modelName: ModelName;
  showIdWhen?: "No name available" | "always" | "never";
  mark?: "jamezz" | "quoted" | "none";
  inheritColor?: boolean;
  renderIdAs?: "subtext" | "subheading";
  removed?: boolean;
}): ReactElement {
  let shouldShowId = false;
  if (showIdWhen === "always") {
    shouldShowId = true;
  } else if (showIdWhen === "No name available") {
    shouldShowId = name == null || name.length === 0;
  }

  if ("naam" in instance && !("name" in instance)) {
    console.error(new Error("Model is not adequately transformed using a Resource in the backoffice."));
    name = instance.naam as string;
  }

  return (
    <span
      key={`mi-${name}-${id}`}
      style={{
        color: "currentcolor",
      }}
    >
      {mark === "jamezz" ? (
        <span
          style={{
            color: inheritColor ? "currentcolor" : "#f48740",
            fontFamily: "Montserrat, sans-serif",
            letterSpacing: "6px",
          }}
        >
          //&nbsp;
        </span>
      ) : mark === "quoted" ? (
        '"'
      ) : null}
      <span
        style={{
          fontSize: "inherit",
        }}
      >
        {name != undefined && name.length > 0 ? name : nameMapping[modelName]}
      </span>
      {shouldShowId ? (
        renderIdAs === "subtext" ? (
          <sub
            style={{
              color: inheritColor ? "currentcolor" : grey[700],
            }}
          >
            {id}
          </sub>
        ) : renderIdAs === "subheading" ? (
          <Typography color={grey[700]} variant={"subtitle2"}>
            #{id}
          </Typography>
        ) : null
      ) : null}
      {mark === "quoted" ? '"' : null}
    </span>
  );
}
